import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { OwnedReview } from "../../../worker/services/analytics";
import { api } from "../../lib/api";
import { Spinner, Empty } from "../../components/States";
import { VerificationBadge } from "../../components/Badge";
import { Stars } from "../../components/Stars";
import { TimeAgo } from "../../lib/format";

// Right-of-reply and disputes (SPEC §2.6, §7).
//
// One public response per review, enforced by a UNIQUE constraint server-side
// and reflected here: once answered, the form is replaced by the response.

export function Reviews() {
  const [items, setItems] = useState<OwnedReview[] | null>(null);
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [disputeOn, setDisputeOn] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = () => api.dashReviews().then((r) => setItems(r.items)).catch(() => setItems([]));
  useEffect(() => { void load(); }, []);

  const submitReply = async (id: string) => {
    setBusy(true);
    setErr(null);
    try {
      await api.respondToReview(id, text);
      setReplyTo(null);
      setText("");
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const submitDispute = async (id: string) => {
    setBusy(true);
    setErr(null);
    try {
      await api.disputeReview(id, text);
      setDisputeOn(null);
      setText("");
      await load();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!items) return <Spinner />;
  if (items.length === 0) {
    return <Empty title="No reviews yet" hint="Reviews of your listings will appear here." />;
  }

  return (
    <div className="narrow-lg">
      <h2>Reviews of your listings</h2>
      <p className="muted fs-md">
        You can post one public response per review. Disputing sends it to moderation with your
        reasons — it does not remove the review, and it never changes your rating.
      </p>

      <div className="grid gap-12">
        {items.map((r) => (
          <article key={r.id} className="card p-16">
            <div className="row between items-baseline">
              <Link to={`/${r.entityType}/${r.entitySlug}`}><strong>{r.entityName}</strong></Link>
              <span className="muted fs-sm"><TimeAgo iso={r.createdAt} /></span>
            </div>

            <div className="row gap-8 my-6">
              <Stars value={r.ratingOverall} />
              <VerificationBadge level={r.verificationLevel as "none"} />
              <span className="muted fs-sm">@{r.authorUsername ?? "someone"}</span>
            </div>

            {r.title && <h3 className="my-4">{r.title}</h3>}
            {r.body && <p className="my-4">{r.body}</p>}

            {r.responseBody ? (
              <div className="card p-12 mt-10">
                <strong className="fs-sm">Your response</strong>
                <p className="m-0">{r.responseBody}</p>
              </div>
            ) : replyTo === r.id ? (
              <div className="mt-10">
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Your public response"
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
                <div className="row gap-8 mt-8">
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void submitReply(r.id)}>
                    Post response
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setReplyTo(null); setText(""); }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : disputeOn === r.id ? (
              <div className="mt-10">
                <textarea
                  className="input"
                  rows={3}
                  placeholder="Why is this review inaccurate? Be specific — a moderator reads this."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
                <div className="row gap-8 mt-8">
                  <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void submitDispute(r.id)}>
                    Submit dispute
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setDisputeOn(null); setText(""); }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="row gap-8 mt-10">
                <button className="btn btn-ghost btn-sm" onClick={() => { setReplyTo(r.id); setText(""); }}>
                  Respond
                </button>
                {r.disputeStatus === "open" ? (
                  <span className="muted fs-sm">Dispute under review</span>
                ) : (
                  <button className="btn btn-ghost btn-sm" onClick={() => { setDisputeOn(r.id); setText(""); }}>
                    Dispute
                  </button>
                )}
                <span className="muted fs-sm">{r.helpfulCount} found this helpful</span>
              </div>
            )}
          </article>
        ))}
      </div>
      {err && <p className="danger-text">{err}</p>}
    </div>
  );
}
